/**
 * FikoStepGeometri — STEP disa aktarim icin mesh hazirlama katmani.
 *
 * Gorevi: sahnedeki (ya da bir GLB'den gelen) dagitik mesh'leri tek, temiz,
 * kaynakli (welded) bir ucgen agina cevirmek. STEP yazicisi bu temiz agi alir.
 *
 * Boru hatti:
 *   topla -> kaynakla -> yumusat -> sadelestir -> cimdik onar -> temizle
 *   -> kenar analizi -> normaller
 *
 * Bu dosya bilerek "yaprak" modul: hicbir FikoCizim* dosyasini import etmez.
 */
import * as THREE from 'three';
import { mergeGeometries, mergeVertices } from 'three/examples/jsm/utils/BufferGeometryUtils.js';

/* ================================================================== */
/* Tipler                                                              */
/* ================================================================== */

/** Kenarlarin kac ucgen tarafindan paylasildiginin dokumu. */
export interface FikoKenarAnalizi {
  /** Tek ucgene komsu kenar = yuzeyde delik/serbest kenar. */
  sinirKenar: number;
  /** 3+ ucgene komsu kenar = yuzey kendi kendine degiyor ("cimdik"). */
  manifoldDisiKenar: number;
  toplamKenar: number;
}

/** Temizlenmis, kaynakli ucgen agi. STEP yazicisinin tek girdisi budur. */
export interface FikoStepMeshVerisi {
  konumlar: Float32Array<ArrayBuffer>;
  ucgenler: Uint32Array<ArrayBuffer>;
  normaller: Float32Array<ArrayBuffer> | null;
  noktaSayisi: number;
  ucgenSayisi: number;
  /** Hem delik hem cimdik yoksa true — yani gercek kapali kati. */
  suGecirmez: boolean;
  sinirKenar: number;
  manifoldDisiKenar: number;
  /** sinir + manifold disi (tek sayida ozet). */
  acikKenarSayisi: number;
  atilanUcgen: number;
  sadelestirmeOncesi: number;
  /** Cimdik onariminda silinen ucgen sayisi. */
  onarimdaSilinen: number;
  /** Cimdik onariminda delik kapatmak icin eklenen ucgen sayisi. */
  onarimdaEklenen: number;
  /** Uyarlanabilir sadelestirmede kac deneme yapildi. */
  sadelestirmeDenemesi: number;
}

export interface FikoStepGeometriSecenek {
  /** Kose kaynak toleransi (mm). Ayni noktaya dusen koseler birlestirilir. */
  kaynakTolerans?: number;
  /** Bu alanin altindaki ucgenler atilir (mm^2). */
  minUcgenAlan?: number;
  /** >0 ise mesh bu ucgen sayisina sadelestirilir (meshoptimizer/WASM). */
  hedefUcgen?: number;
  /**
   * Sadelestirme yuzeyi cimdiklerse hedefi kademeli gevseterek yeniden dener.
   * Kati (solid) cikti sansini artirir, karsiliginda dosya buyuyebilir.
   */
  uyarlanabilirSadelestirme?: boolean;
  /** Uyarlanabilir modda en fazla kac deneme (varsayilan 4). */
  enFazlaDeneme?: number;
  /** Her denemede hedefin carpani (varsayilan 1.4). */
  denemeCarpani?: number;
  /**
   * Manifold disi ("cimdik") kenarlari onarir: bozuk ucgenleri siler,
   * olusan delikleri kapatir. Kapali kati elde etmenin kesin yolu.
   */
  cimdikOnar?: boolean;
  /** Onarim icin en fazla tur (varsayilan 3). */
  onarimTuru?: number;
  /** Laplace yumusatma adimi (0 = kapali). */
  yumusatmaAdim?: number;
  /** Yumusatma gucu 0..1 (varsayilan 0.5). */
  yumusatmaGuc?: number;
  /** Normaller hesaplansin mi. */
  normalUret?: boolean;
  ilerleme?: (mesaj: string, yuzde: number) => void;
}

/* ================================================================== */
/* 1) Sahnedeki mesh'leri tek geometriye topla                         */
/* ================================================================== */

/**
 * Bir Object3D agacindaki tum gorunur Mesh'leri dunya koordinatlarinda
 * tek bir BufferGeometry'de birlestirir.
 *
 * Yalnizca "position" tasinir. Bu sadece sadelik degil, DOGRULUK meselesi:
 * mergeVertices tum oznitelikleri karsilastirdigi icin, uv/normal farkli
 * olan dikis (seam) koseleri kaynaklanmaz ve su gecirmez bir model bile
 * "delik dolu" gorunur. Oznitelikleri bastan atinca bu tuzak kalkiyor.
 */
export function fikoStepMeshleriTopla(kok: THREE.Object3D): THREE.BufferGeometry {
  if (!kok) throw new Error('STEP icin obje yok.');
  kok.updateMatrixWorld(true);

  const parcalar: THREE.BufferGeometry[] = [];

  kok.traverse((e: any) => {
    if (!e?.isMesh || e.visible === false) return;
    const g: THREE.BufferGeometry = e.geometry;
    if (!g?.getAttribute?.('position')) return;

    // Kenar cizgileri (LineSegments) isMesh degil, zaten elenir.
    const p = g.index ? g.toNonIndexed() : g.clone();

    for (const ad of Object.keys(p.attributes)) {
      if (ad !== 'position') p.deleteAttribute(ad);
    }
    p.morphAttributes = {};
    p.clearGroups();

    e.updateWorldMatrix(true, false);
    p.applyMatrix4(e.matrixWorld);

    parcalar.push(p);
  });

  if (!parcalar.length) {
    throw new Error("Bu ogede STEP'e aktarilacak yuzey (mesh) bulunamadi.");
  }

  const birlesik = parcalar.length === 1 ? parcalar[0] : mergeGeometries(parcalar, false);
  if (!birlesik) throw new Error('Mesh parcalari birlestirilemedi.');
  if (parcalar.length > 1) for (const p of parcalar) p.dispose();

  return birlesik;
}

/* ================================================================== */
/* 2) Kenar analizi                                                    */
/* ================================================================== */

/** Kenar anahtari (yonsuz). */
function kenarAnahtari(p: number, q: number, taban: number): number {
  return p < q ? p * taban + q : q * taban + p;
}

/** Kenar -> komsu ucgen sayisi haritasi. */
function kenarSayaci(ucgenler: Uint32Array, taban: number): Map<number, number> {
  const say = new Map<number, number>();
  for (let i = 0; i < ucgenler.length; i += 3) {
    const a = ucgenler[i], b = ucgenler[i + 1], c = ucgenler[i + 2];
    const k1 = kenarAnahtari(a, b, taban);
    const k2 = kenarAnahtari(b, c, taban);
    const k3 = kenarAnahtari(c, a, taban);
    say.set(k1, (say.get(k1) || 0) + 1);
    say.set(k2, (say.get(k2) || 0) + 1);
    say.set(k3, (say.get(k3) || 0) + 1);
  }
  return say;
}

/**
 * Her kenarin kac ucgene komsu oldugunu sayar.
 *
 * STEP acisindan kritik: CLOSED_SHELL'de her kenar TAM 2 yuz tarafindan
 * paylasilmalidir (ISO 10303-42). 1 = delik, 3+ = cimdik. Ikisi de kati
 * gecerliligini bozar ama tedavileri farkli oldugu icin ayri sayiyoruz.
 */
export function fikoKenarAnaliz(ucgenler: Uint32Array, noktaSayisi: number): FikoKenarAnalizi {
  const say = kenarSayaci(ucgenler, noktaSayisi + 1);
  let sinir = 0, manifoldDisi = 0;
  for (const n of say.values()) {
    if (n === 1) sinir++;
    else if (n > 2) manifoldDisi++;
  }
  return { sinirKenar: sinir, manifoldDisiKenar: manifoldDisi, toplamKenar: say.size };
}

/* ================================================================== */
/* 3) Bagli bilesenler (ayri govdeler)                                 */
/* ================================================================== */

export interface FikoStepBilesen {
  ucgenIndisleri: Uint32Array;
  kapali: boolean;
  sinirKenar: number;
  manifoldDisiKenar: number;
}

/**
 * Mesh'i birbirine degmeyen ayri govdelere boler (union-find).
 *
 * CLOSED_SHELL bir "connected_face_set"tir, yani BAGLI olmak zorundadir.
 * Iki ayri cismi tek kabuga koymak sema ihlali; hosgorulu okuyucular yutar
 * ama CATIA gibi kati cekirdekler reddedebilir.
 */
export function fikoStepBilesenleriAyir(
  ucgenler: Uint32Array,
  noktaSayisi: number
): FikoStepBilesen[] {
  const ucgenSayisi = ucgenler.length / 3;
  if (ucgenSayisi === 0) return [];

  const ata = new Int32Array(noktaSayisi);
  for (let i = 0; i < noktaSayisi; i++) ata[i] = i;

  const bul = (x: number): number => {
    let k = x;
    while (ata[k] !== k) { ata[k] = ata[ata[k]]; k = ata[k]; }
    return k;
  };
  const birlestir = (a: number, c: number): void => {
    const ra = bul(a), rc = bul(c);
    if (ra !== rc) ata[ra] = rc;
  };

  for (let i = 0; i < ucgenler.length; i += 3) {
    birlestir(ucgenler[i], ucgenler[i + 1]);
    birlestir(ucgenler[i + 1], ucgenler[i + 2]);
  }

  const kokDizin = new Map<number, number>();
  const gruplar: number[][] = [];
  for (let t = 0; t < ucgenSayisi; t++) {
    const kok = bul(ucgenler[t * 3]);
    let d = kokDizin.get(kok);
    if (d === undefined) { d = gruplar.length; kokDizin.set(kok, d); gruplar.push([]); }
    gruplar[d].push(t);
  }

  return gruplar.map(liste => {
    const indisler = Uint32Array.from(liste);
    const alt = new Uint32Array(indisler.length * 3);
    for (let i = 0; i < indisler.length; i++) {
      const t = indisler[i];
      alt[i * 3] = ucgenler[t * 3];
      alt[i * 3 + 1] = ucgenler[t * 3 + 1];
      alt[i * 3 + 2] = ucgenler[t * 3 + 2];
    }
    const an = fikoKenarAnaliz(alt, noktaSayisi);
    return {
      ucgenIndisleri: indisler,
      kapali: an.sinirKenar === 0 && an.manifoldDisiKenar === 0,
      sinirKenar: an.sinirKenar,
      manifoldDisiKenar: an.manifoldDisiKenar
    };
  });
}

/* ================================================================== */
/* 4) Kaynakla (weld)                                                  */
/* ================================================================== */

/**
 * Ayni noktaya (toleransa gore) dusen koseleri birlestirir.
 * BufferGeometryUtils.mergeVertices sadece "position" oznitelikli bir
 * geometride calisirsa gercekten kose sayisini dusurur; toplama asamasi
 * (fikoStepMeshleriTopla) bunu garanti eder.
 */
export function fikoKaynakla(geo: THREE.BufferGeometry, tolerans = 0.001): THREE.BufferGeometry {
  return mergeVertices(geo, tolerans);
}

/* ================================================================== */
/* 5) Dejenere / cok kucuk ucgenleri temizle                           */
/* ================================================================== */

export function fikoUcgenTemizle(
  geo: THREE.BufferGeometry,
  minAlan = 1e-6
): { geo: THREE.BufferGeometry; atilan: number } {
  const pos = geo.getAttribute('position') as THREE.BufferAttribute;
  const idx = geo.getIndex();
  const ucgenler: number[] = [];
  let atilan = 0;

  const a = new THREE.Vector3(), b = new THREE.Vector3(), c = new THREE.Vector3();
  const ab = new THREE.Vector3(), ac = new THREE.Vector3(), cross = new THREE.Vector3();

  const toplamIndis = idx ? idx.count : pos.count;
  for (let i = 0; i < toplamIndis; i += 3) {
    const ia = idx ? idx.getX(i) : i;
    const ib = idx ? idx.getX(i + 1) : i + 1;
    const ic = idx ? idx.getX(i + 2) : i + 2;
    if (ia === ib || ib === ic || ia === ic) { atilan++; continue; }

    a.set(pos.getX(ia), pos.getY(ia), pos.getZ(ia));
    b.set(pos.getX(ib), pos.getY(ib), pos.getZ(ib));
    c.set(pos.getX(ic), pos.getY(ic), pos.getZ(ic));
    ab.subVectors(b, a);
    ac.subVectors(c, a);
    cross.crossVectors(ab, ac);
    const alan = cross.length() * 0.5;
    if (!isFinite(alan) || alan < minAlan) { atilan++; continue; }

    ucgenler.push(ia, ib, ic);
  }

  const yeni = geo.clone();
  yeni.setIndex(ucgenler);
  return { geo: yeni, atilan };
}

/* ================================================================== */
/* 6) Laplace yumusatma                                                */
/* ================================================================== */

/**
 * Her koseyi komsularinin agirlik merkezine dogru "guc" oraninda ceker.
 * Topolojiyi (ucgenler) degistirmez, yalnizca konumlari yumusatir.
 */
export function fikoYumusat(
  konumlar: Float32Array,
  ucgenler: Uint32Array,
  adim = 1,
  guc = 0.5
): Float32Array<ArrayBuffer> {
  const noktaSayisi = konumlar.length / 3;
  const komsu: Set<number>[] = new Array(noktaSayisi);
  for (let i = 0; i < noktaSayisi; i++) komsu[i] = new Set<number>();

  for (let i = 0; i < ucgenler.length; i += 3) {
    const a = ucgenler[i], b = ucgenler[i + 1], c = ucgenler[i + 2];
    komsu[a].add(b); komsu[a].add(c);
    komsu[b].add(a); komsu[b].add(c);
    komsu[c].add(a); komsu[c].add(b);
  }

  let mevcut: Float32Array<ArrayBuffer> = new Float32Array(konumlar.length);
  mevcut.set(konumlar);
  for (let s = 0; s < adim; s++) {
    const yeni: Float32Array<ArrayBuffer> = new Float32Array(mevcut.length);
    yeni.set(mevcut);
    for (let p = 0; p < noktaSayisi; p++) {
      const kom = komsu[p];
      if (kom.size === 0) continue;
      let sx = 0, sy = 0, sz = 0;
      for (const k of kom) { sx += mevcut[k * 3]; sy += mevcut[k * 3 + 1]; sz += mevcut[k * 3 + 2]; }
      const n = kom.size;
      const ox = mevcut[p * 3], oy = mevcut[p * 3 + 1], oz = mevcut[p * 3 + 2];
      yeni[p * 3] = ox + (sx / n - ox) * guc;
      yeni[p * 3 + 1] = oy + (sy / n - oy) * guc;
      yeni[p * 3 + 2] = oz + (sz / n - oz) * guc;
    }
    mevcut = yeni;
  }
  return mevcut;
}

/* ================================================================== */
/* 7) Uyarlanabilir sadelestirme (meshoptimizer / WASM)                */
/* ================================================================== */

export async function fikoSadelestir(
  konumlar: Float32Array,
  ucgenler: Uint32Array,
  hedefUcgen: number,
  uyarlanabilir = true,
  enFazlaDeneme = 4,
  denemeCarpani = 1.4
): Promise<{ ucgenler: Uint32Array<ArrayBuffer>; deneme: number }> {
  const kopyala = (kaynak: ArrayLike<number>): Uint32Array<ArrayBuffer> => {
    const hedef = new Uint32Array(kaynak.length);
    hedef.set(kaynak);
    return hedef;
  };

  const { MeshoptSimplifier } = await import('meshoptimizer');
  await MeshoptSimplifier.ready;

  if (!MeshoptSimplifier.supported || ucgenler.length === 0) {
    return { ucgenler: kopyala(ucgenler), deneme: 0 };
  }

  let hedefIndeks = Math.max(hedefUcgen, 4) * 3;
  let sonUcgen: Uint32Array<ArrayBuffer> = kopyala(ucgenler);
  const enFazla = uyarlanabilir ? Math.max(1, enFazlaDeneme) : 1;
  let deneme = 0;

  for (deneme = 1; deneme <= enFazla; deneme++) {
    const hedef = Math.min(hedefIndeks, ucgenler.length);
    const [sonuc] = MeshoptSimplifier.simplify(
      ucgenler,
      konumlar,
      3,
      hedef,
      0.02,
      ['LockBorder']
    );
    if (sonuc && sonuc.length >= 9) { sonUcgen = kopyala(sonuc); break; }
    hedefIndeks = Math.round(hedefIndeks * denemeCarpani);
    if (hedefIndeks >= ucgenler.length) { sonUcgen = kopyala(ucgenler); break; }
  }

  return { ucgenler: sonUcgen, deneme };
}

/* ================================================================== */
/* 8) Cimdik (manifold-disi) onarimi                                   */
/* ================================================================== */

/** Sinir kenarlarindan kapali halkalar (delik agizlari) cikarir. */
function fikoSinirHalkalariniBul(
  ucgenler: Uint32Array,
  taban: number,
  kenarSay: Map<number, number>
): number[][] {
  const sonrakiMap = new Map<number, number>();
  for (let i = 0; i < ucgenler.length; i += 3) {
    const uc = [ucgenler[i], ucgenler[i + 1], ucgenler[i + 2]];
    for (let k = 0; k < 3; k++) {
      const p = uc[k], q = uc[(k + 1) % 3];
      const anahtar = kenarAnahtari(p, q, taban);
      if (kenarSay.get(anahtar) === 1) sonrakiMap.set(p, q);
    }
  }

  const ziyaret = new Set<number>();
  const halkalar: number[][] = [];
  for (const baslangic of sonrakiMap.keys()) {
    if (ziyaret.has(baslangic)) continue;
    const halka: number[] = [];
    let mevcut = baslangic;
    let guvenlik = 0;
    while (!ziyaret.has(mevcut) && guvenlik++ < 20000) {
      ziyaret.add(mevcut);
      halka.push(mevcut);
      const sonraki = sonrakiMap.get(mevcut);
      if (sonraki === undefined) break;
      mevcut = sonraki;
      if (mevcut === baslangic) break;
    }
    if (halka.length >= 3) halkalar.push(halka);
  }
  return halkalar;
}

/** Kucuk delikleri (en fazla `enFazlaKose` kenarli) yelpaze ucgenlemeyle kapatir. */
function fikoKucukDelikleriKapat(
  ucgenler: Uint32Array,
  noktaSayisi: number,
  hedefListe: number[],
  enFazlaKose = 10
): number {
  const taban = noktaSayisi + 1;
  const sayac = kenarSayaci(ucgenler, taban);
  const halkalar = fikoSinirHalkalariniBul(ucgenler, taban, sayac);
  let eklenen = 0;
  for (const halka of halkalar) {
    if (halka.length < 3 || halka.length > enFazlaKose) continue;
    for (let i = 1; i < halka.length - 1; i++) {
      hedefListe.push(halka[0], halka[i], halka[i + 1]);
      eklenen++;
    }
  }
  return eklenen;
}

/**
 * Manifold-disi ("cimdik") kenarlara komsu ucgenleri siler, sonra acilan
 * kucuk delikleri yelpaze ucgenlemeyle kapatir. Birden fazla tur calisir
 * cunku bir turdeki silme yeni sinir kenarlari (ve bazen yeni cimdikler)
 * ortaya cikarabilir.
 */
export function fikoCimdikOnar(
  konumlar: Float32Array,
  ucgenler: Uint32Array,
  turSayisi = 3
): { ucgenler: Uint32Array<ArrayBuffer>; silinen: number; eklenen: number } {
  const noktaSayisi = konumlar.length / 3;
  let mevcut: number[] = Array.from(ucgenler);
  let toplamSilinen = 0;
  let toplamEklenen = 0;

  for (let tur = 0; tur < Math.max(1, turSayisi); tur++) {
    const ucArr = Uint32Array.from(mevcut);
    const taban = noktaSayisi + 1;
    const sayac = kenarSayaci(ucArr, taban);

    const silinecek = new Set<number>();
    const ucgenSayisi = ucArr.length / 3;
    for (let t = 0; t < ucgenSayisi; t++) {
      const a = ucArr[t * 3], b = ucArr[t * 3 + 1], c = ucArr[t * 3 + 2];
      const k1 = kenarAnahtari(a, b, taban);
      const k2 = kenarAnahtari(b, c, taban);
      const k3 = kenarAnahtari(c, a, taban);
      if ((sayac.get(k1) || 0) > 2 || (sayac.get(k2) || 0) > 2 || (sayac.get(k3) || 0) > 2) {
        silinecek.add(t);
      }
    }

    if (silinecek.size === 0 && tur > 0) break;

    const kalanlar: number[] = [];
    for (let t = 0; t < ucgenSayisi; t++) {
      if (silinecek.has(t)) continue;
      kalanlar.push(ucArr[t * 3], ucArr[t * 3 + 1], ucArr[t * 3 + 2]);
    }
    toplamSilinen += silinecek.size;
    mevcut = kalanlar;

    if (silinecek.size === 0) continue;

    const eklenen = fikoKucukDelikleriKapat(Uint32Array.from(mevcut), noktaSayisi, mevcut);
    toplamEklenen += eklenen;

    if (silinecek.size === 0 && eklenen === 0) break;
  }

  const ucgenlerSonuc: Uint32Array<ArrayBuffer> = new Uint32Array(mevcut.length);
  ucgenlerSonuc.set(mevcut);
  return { ucgenler: ucgenlerSonuc, silinen: toplamSilinen, eklenen: toplamEklenen };
}

/* ================================================================== */
/* 9) Normal uretimi (alan agirlikli, koseye gore ortalanmis)          */
/* ================================================================== */

export function fikoNormalUret(konumlar: Float32Array, ucgenler: Uint32Array): Float32Array<ArrayBuffer> {
  const noktaSayisi = konumlar.length / 3;
  const normaller = new Float32Array(noktaSayisi * 3);
  const a = new THREE.Vector3(), b = new THREE.Vector3(), c = new THREE.Vector3();
  const ab = new THREE.Vector3(), ac = new THREE.Vector3(), n = new THREE.Vector3();

  for (let t = 0; t < ucgenler.length; t += 3) {
    const ia = ucgenler[t], ib = ucgenler[t + 1], ic = ucgenler[t + 2];
    a.set(konumlar[ia * 3], konumlar[ia * 3 + 1], konumlar[ia * 3 + 2]);
    b.set(konumlar[ib * 3], konumlar[ib * 3 + 1], konumlar[ib * 3 + 2]);
    c.set(konumlar[ic * 3], konumlar[ic * 3 + 1], konumlar[ic * 3 + 2]);
    ab.subVectors(b, a);
    ac.subVectors(c, a);
    n.crossVectors(ab, ac); // alan ile orantili — buyuk ucgenler daha agir katkida bulunur

    normaller[ia * 3] += n.x; normaller[ia * 3 + 1] += n.y; normaller[ia * 3 + 2] += n.z;
    normaller[ib * 3] += n.x; normaller[ib * 3 + 1] += n.y; normaller[ib * 3 + 2] += n.z;
    normaller[ic * 3] += n.x; normaller[ic * 3 + 1] += n.y; normaller[ic * 3 + 2] += n.z;
  }

  for (let p = 0; p < noktaSayisi; p++) {
    const x = normaller[p * 3], y = normaller[p * 3 + 1], z = normaller[p * 3 + 2];
    const uzunluk = Math.hypot(x, y, z) || 1;
    normaller[p * 3] = x / uzunluk;
    normaller[p * 3 + 1] = y / uzunluk;
    normaller[p * 3 + 2] = z / uzunluk;
  }

  return normaller;
}

/* ================================================================== */
/* 10) Orkestrator — tum boru hattini calistirir                       */
/* ================================================================== */

/**
 * Sahnedeki (ya da tek bir objedeki) mesh'leri STEP yazicisina hazir
 * hale getirir: topla -> kaynakla -> temizle -> [yumusat] -> [sadelestir]
 * -> [cimdik onar] -> kenar analizi -> [normaller].
 */
export async function fikoStepMeshHazirla(
  kok: THREE.Object3D,
  secenek: FikoStepGeometriSecenek = {}
): Promise<FikoStepMeshVerisi> {
  const ilerle = secenek.ilerleme || (() => { });

  ilerle('Mesh toplaniyor', 5);
  let geo = fikoStepMeshleriTopla(kok);
  const idx0 = geo.getIndex();
  const pos0 = geo.getAttribute('position') as THREE.BufferAttribute;
  const sadelestirmeOncesiUcgen = (idx0 ? idx0.count : pos0.count) / 3;

  ilerle('Koseler kaynaklaniyor', 15);
  geo = fikoKaynakla(geo, secenek.kaynakTolerans ?? 0.001);

  ilerle('Bozuk ucgenler temizleniyor', 25);
  const temiz = fikoUcgenTemizle(geo, secenek.minUcgenAlan ?? 1e-6);
  geo = temiz.geo;
  const atilanUcgen = temiz.atilan;

  const posAttr = geo.getAttribute('position') as THREE.BufferAttribute;
  const idxAttr = geo.getIndex() as THREE.BufferAttribute;
  const posKaynak = posAttr.array as ArrayLike<number>;
  const idxKaynak = idxAttr.array as ArrayLike<number>;
  let konumlar: Float32Array<ArrayBuffer> = new Float32Array(posKaynak.length);
  konumlar.set(posKaynak);
  let ucgenler: Uint32Array<ArrayBuffer> = new Uint32Array(idxKaynak.length);
  ucgenler.set(idxKaynak);

  if (secenek.yumusatmaAdim && secenek.yumusatmaAdim > 0) {
    ilerle('Yuzey yumusatiliyor', 35);
    konumlar = fikoYumusat(konumlar, ucgenler, secenek.yumusatmaAdim, secenek.yumusatmaGuc ?? 0.5);
  }

  let sadelestirmeDenemesi = 0;
  if (secenek.hedefUcgen && secenek.hedefUcgen > 0 && secenek.hedefUcgen < ucgenler.length / 3) {
    ilerle('Sadelestiriliyor', 50);
    const sonuc = await fikoSadelestir(
      konumlar,
      ucgenler,
      secenek.hedefUcgen,
      secenek.uyarlanabilirSadelestirme ?? true,
      secenek.enFazlaDeneme ?? 4,
      secenek.denemeCarpani ?? 1.4
    );
    ucgenler = sonuc.ucgenler;
    sadelestirmeDenemesi = sonuc.deneme;
  }

  let onarimdaSilinen = 0, onarimdaEklenen = 0;
  if (secenek.cimdikOnar) {
    ilerle('Cimdikler onariliyor', 70);
    const sonuc = fikoCimdikOnar(konumlar, ucgenler, secenek.onarimTuru ?? 3);
    ucgenler = sonuc.ucgenler;
    onarimdaSilinen = sonuc.silinen;
    onarimdaEklenen = sonuc.eklenen;
  }

  ilerle('Kenarlar analiz ediliyor', 85);
  const noktaSayisi = konumlar.length / 3;
  const analiz = fikoKenarAnaliz(ucgenler, noktaSayisi);

  let normaller: Float32Array<ArrayBuffer> | null = null;
  if (secenek.normalUret ?? true) {
    ilerle('Normaller hesaplaniyor', 95);
    normaller = fikoNormalUret(konumlar, ucgenler);
  }

  ilerle('Hazir', 100);

  return {
    konumlar,
    ucgenler,
    normaller,
    noktaSayisi,
    ucgenSayisi: ucgenler.length / 3,
    suGecirmez: analiz.sinirKenar === 0 && analiz.manifoldDisiKenar === 0,
    sinirKenar: analiz.sinirKenar,
    manifoldDisiKenar: analiz.manifoldDisiKenar,
    acikKenarSayisi: analiz.sinirKenar + analiz.manifoldDisiKenar,
    atilanUcgen,
    sadelestirmeOncesi: sadelestirmeOncesiUcgen,
    onarimdaSilinen,
    onarimdaEklenen,
    sadelestirmeDenemesi
  };
}
